import { zodResolver } from "@hookform/resolvers/zod";
import { Loader, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateOrganizationMutation } from "@/data/mutations";

const createOrganizationFormSchema = z.object({
  name: z.string().nonempty(),
});

export const CreateOrganizationDialog = () => {
  const [open, setOpen] = useState(false);

  const createOrganizationMutation = useCreateOrganizationMutation();
  const form = useForm({
    resolver: zodResolver(createOrganizationFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const handleSubmit = async (
    values: z.infer<typeof createOrganizationFormSchema>,
  ) => {
    try {
      await createOrganizationMutation.mutateAsync(values.name);
    } catch (error) {
      console.error(error);
    } finally {
      setOpen(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button>
          <Plus /> Create Organization
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <AlertDialogHeader>
              <AlertDialogTitle>Create Organization</AlertDialogTitle>
              <AlertDialogDescription>
                Enter in the name or the organization that you would like to
                create. It is recommended to not give unique names to each
                organization.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="my-6">
                  <FormLabel>Organization Name*</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Studios" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={createOrganizationMutation.isPending}
              >
                Cancel
              </AlertDialogCancel>
              <Button
                type="submit"
                disabled={createOrganizationMutation.isPending}
              >
                {createOrganizationMutation.isPending ? (
                  <Loader className="animate-spin" />
                ) : null}
                Create
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
